/**
 * Funding & Withdrawals — phase 6 of broker integration.
 *
 * Supported rails (Nigerian retail-focused):
 *   NIBSS_INSTANT    — NIP instant transfer (free for retail, T+0)
 *   NIBSS_NEFT       — NEFT bulk (1 hour, free)
 *   CARD             — Paystack / Interswitch (instant, 1.5% + N100 capped
 *                      at N2,000; no fee > N2000 threshold applies)
 *   BANK_DD          — Direct Debit from NUBAN (T+1, free)
 *   BROKER_TRANSFER  — Broker-to-broker sweep (internal; free)
 *
 * Withdrawals follow CBN 2022 customer-withdrawal guidelines: min N1,000,
 * daily cap N5M for Tier-3 KYC. Withdrawals from SELL proceeds only settle
 * after T+2 (equities/ETFs) or T+2 (bonds); the UI surfaces the earliest
 * withdrawable date per tranche.
 *
 * Every transfer gets an idempotency key + audit record in S3 Object-Lock
 * (7-year COMPLIANCE retention), keyed by {cognitoSub, rail, brokerId, ts}.
 */

export type Rail = 'NIBSS_INSTANT' | 'NIBSS_NEFT' | 'CARD' | 'BANK_DD' | 'BROKER_TRANSFER';
export type Direction = 'DEPOSIT' | 'WITHDRAWAL';
export type TransferStatus = 'PENDING' | 'PROCESSING' | 'SETTLED' | 'FAILED' | 'CANCELLED';

export interface RailSpec {
  id: Rail;
  label: string;
  glyph: string;
  instant: boolean;
  minKobo: bigint;
  maxKobo: bigint;
  feeFixedKobo: bigint;
  feeBps: bigint;          // in bps (e.g. 150 = 1.50%)
  feeCapKobo: bigint;      // 0n = no cap
  description: string;
}

export const RAILS: RailSpec[] = [
  { id: 'NIBSS_INSTANT', label: 'NIBSS Instant (NIP)', glyph: '\u26A1', instant: true,
    minKobo: 100_000n, maxKobo: 1_000_000_000n, feeFixedKobo: 0n, feeBps: 0n, feeCapKobo: 0n,
    description: 'Instant bank transfer via NIBSS NIP. No fee for retail. Funds credit within 30 seconds.' },
  { id: 'NIBSS_NEFT',    label: 'NEFT (Bulk)',        glyph: '\u2709', instant: false,
    minKobo: 100_000n, maxKobo: 5_000_000_000n, feeFixedKobo: 0n, feeBps: 0n, feeCapKobo: 0n,
    description: 'NIBSS NEFT bulk transfer. Settlement within 1 hour during business days.' },
  { id: 'CARD',          label: 'Card (Paystack)',    glyph: '\u25A1', instant: true,
    minKobo: 10_000n, maxKobo: 200_000_000n, feeFixedKobo: 10_000n, feeBps: 150n, feeCapKobo: 200_000n,
    description: 'Debit/credit card via Paystack. 1.5% + \u20A6100 capped at \u20A62,000. No fees above \u20A62,000 threshold.' },
  { id: 'BANK_DD',       label: 'Direct Debit',       glyph: '\u25D0', instant: false,
    minKobo: 100_000n, maxKobo: 5_000_000_000n, feeFixedKobo: 0n, feeBps: 0n, feeCapKobo: 0n,
    description: 'Direct debit from your NUBAN. T+1 settlement. Free.' },
  { id: 'BROKER_TRANSFER', label: 'Broker Sweep',    glyph: '\u21C4', instant: true,
    minKobo: 10_000n, maxKobo: 10_000_000_000n, feeFixedKobo: 0n, feeBps: 0n, feeCapKobo: 0n,
    description: 'Internal sweep between linked brokers. Instant, no fees.' },
];

export function railById(id: Rail): RailSpec | undefined {
  return RAILS.find((r) => r.id === id);
}

/** CBN Tier-3 retail daily limits as of 2026-Q1. */
export const DAILY_LIMIT_KOBO: Record<Direction, bigint> = {
  DEPOSIT:    50_000_000_000n, // N500M/day
  WITHDRAWAL:  500_000_000n,    // N5M/day
};

export interface FeeBreakdown {
  amountKobo: bigint;
  feeKobo: bigint;
  netKobo: bigint;       // amount - fee for withdrawal, amount for deposit (fee deducted separately)
  rate: {
    fixedKobo: string;
    bps: string;
    capKobo: string;
  };
}

export function computeFee(rail: Rail, amountKobo: bigint, direction: Direction): FeeBreakdown {
  const spec = railById(rail);
  if (!spec) throw new Error('unknown rail: ' + rail);
  if (amountKobo < spec.minKobo) throw new Error('below min for ' + rail);
  if (amountKobo > spec.maxKobo) throw new Error('above max for ' + rail);

  const variable = (amountKobo * spec.feeBps) / 10000n;
  let fee = spec.feeFixedKobo + variable;
  if (spec.feeCapKobo > 0n && fee > spec.feeCapKobo) fee = spec.feeCapKobo;

  // Deposits: fee is charged separately by the processor, net == amount.
  // Withdrawals: fee deducted from amount, net is what the user receives.
  const net = direction === 'WITHDRAWAL' ? amountKobo - fee : amountKobo;

  return {
    amountKobo,
    feeKobo: fee,
    netKobo: net,
    rate: {
      fixedKobo: spec.feeFixedKobo.toString(),
      bps: spec.feeBps.toString(),
      capKobo: spec.feeCapKobo.toString(),
    },
  };
}

export interface ValidateInput {
  direction: Direction;
  rail: Rail;
  amountKobo: bigint;
  availableCashKobo?: bigint;    // for withdrawal
  dailyUsedKobo: bigint;
}
export interface ValidationResult {
  ok: boolean;
  errors: string[];
  warnings: string[];
}
export function validateTransfer(i: ValidateInput): ValidationResult {
  const errors: string[] = [], warnings: string[] = [];
  const spec = railById(i.rail);
  if (!spec) { errors.push('Unknown rail'); return { ok: false, errors, warnings }; }
  if (i.amountKobo <= 0n) errors.push('Amount must be positive');
  if (i.amountKobo < spec.minKobo) errors.push('Below minimum for this rail');
  if (i.amountKobo > spec.maxKobo) errors.push('Above maximum for this rail');
  if (i.direction === 'WITHDRAWAL' && i.availableCashKobo !== undefined && i.amountKobo > i.availableCashKobo)
    errors.push('Insufficient settled cash (remember T+2 on sales proceeds)');
  const limit = DAILY_LIMIT_KOBO[i.direction];
  if (i.dailyUsedKobo + i.amountKobo > limit) errors.push('Exceeds CBN Tier-3 daily limit');
  else if (i.dailyUsedKobo + i.amountKobo > (limit * 80n) / 100n)
    warnings.push('This will use >80% of your daily limit.');
  return { ok: errors.length === 0, errors, warnings };
}

export interface Transfer {
  id: string;
  direction: Direction;
  rail: Rail;
  brokerId: string;
  amountKobo: string;
  feeKobo: string;
  status: TransferStatus;
  initiatedAt: string;
  settlesAt?: string;
  reference?: string;
  reason?: string;         // populated on FAILED
}

export function seedDemoTransfers(): Transfer[] {
  const now = Date.now();
  const iso = (ms: number) => new Date(ms).toISOString();
  return [
    { id: 'tx-001', direction: 'DEPOSIT', rail: 'NIBSS_INSTANT', brokerId: 'bamboo',
      amountKobo: '500000000', feeKobo: '0', status: 'SETTLED',
      initiatedAt: iso(now - 2 * 3600_000), settlesAt: iso(now - 2 * 3600_000 + 28_000),
      reference: 'NIP-5502114982' },
    { id: 'tx-002', direction: 'DEPOSIT', rail: 'CARD', brokerId: 'bamboo',
      amountKobo: '200000000', feeKobo: '200000', status: 'SETTLED',
      initiatedAt: iso(now - 12 * 3600_000), settlesAt: iso(now - 12 * 3600_000 + 5_000),
      reference: 'PSK-47291' },
    { id: 'tx-003', direction: 'WITHDRAWAL', rail: 'NIBSS_INSTANT', brokerId: 'chd',
      amountKobo: '75000000', feeKobo: '0', status: 'PROCESSING',
      initiatedAt: iso(now - 15 * 60_000),
      reference: 'WDR-CHD-00412' },
    { id: 'tx-004', direction: 'DEPOSIT', rail: 'BANK_DD', brokerId: 'meristem',
      amountKobo: '1500000000', feeKobo: '0', status: 'PENDING',
      initiatedAt: iso(now - 40 * 60_000),
      settlesAt: iso(now + 8 * 3600_000) },
    { id: 'tx-005', direction: 'WITHDRAWAL', rail: 'CARD', brokerId: 'bamboo',
      amountKobo: '50000000', feeKobo: '75000', status: 'FAILED',
      initiatedAt: iso(now - 3 * 86400_000),
      reason: 'card_expired' },
  ];
}
